import { IAnnotation, IItemReference } from '@augloop/types-core';

// Error priority level to be shown to the client app which will be interpreted as different squiggle colors (or not squiggle)
export enum Priority {
  High = 0,
  Medium = 1,
  Low = 2,
  Informational = 3
}

// Actions that a critique supports, expressed as flags that can be combined.
export enum CritiqueAction {
  NotificationOnly = 0,
  Delete = 1 << 0,
  Ignore = 1 << 1,
  IgnoreAll = 1 << 2,
  IgnoreCritiqueType = 1 << 3,
  AddToDictionary = 1 << 4,
  ShowDetails = 1 << 5
}

// Actions that a suggestion supports, expressed as flags that can be combined.
export enum SuggestionAction {
  None = 0,
  ChangeOnce = 1 << 0,
  ChangeAll = 1 << 1,
  AutoCorrect = 1 << 2,
  ReadAloud = 1 << 3,
  PersonalizedAutoCorrect = 1 << 4
}

export interface Range {
  start: number;
  length: number;
}

// RangeAnnotation
// Represents an annotation on a range of text
export interface IRangeAnnotation extends IAnnotation {
  // The range of the annotation in the parent node (usually parent tile)
  readonly range: Range;

  // The range of text in which any edit will invalidate the annotation.
  // If undefined, the annotation range is used by default.
  // This can also be set to an explicitly empty range (length 0) to produce an annotation that is never invalidated but instead follows edits.
  readonly invalidationRange?: Range;
}

export interface MultiTileRange {
  /**
   * The tile containing the start position.
   */
  readonly startTile: IItemReference;

  /**
   * The character offset within the start tile indicating the beginning of the range.
   * If not set, all start tile content is included.
   */
  readonly start?: number;

  /**
   * The tile containing the end position.
   */
  readonly endTile: IItemReference;

  /**
   * The (exclusive) character offset within the end tile indicating the end of the range.
   * If not set, all end tile content is included.
   */
  readonly end?: number;
}

/**
 * Represents an annotation on a range of text that spans multiple tiles. The annotation
 * must be attached to a common ancestor item of the start and end tiles in the data model
 * (for example, the Document item). The annotated range includes the entire content of any
 * intermediate tiles between startTile and endTile, in reading order.
 */
export interface IMultiTileRangeAnnotation extends IAnnotation {
  /** The range of the annotation in the data model below the annotated item */
  readonly range: MultiTileRange;
}

// LanguageAnnotation
// Represents an annotation of language on a range of text
export interface ILanguageDetection {
  language: string;
  cultureName: string;
  characterCount: number;
  /**
   * @csharptype double
   * @scriptsharptype double
   * @cpptype double
   * @cppedgetype double
   */
  characterPercentage: number;
}

export interface ILanguageDetectionSummaryAnnotation extends IAnnotation {
  totalCharacterCount: number;
  languageAnnotations: ILanguageDetection[];
}

// LanguageCount Annotation
// Annotation with a summary (count) of all the languages found in the document (grouped by languages)
export interface ILanguageCountAnnotation extends IAnnotation {
  // The language
  language: string;

  // How many characters of the given language are in the document
  characterCount: number;

  // The percentage of the given language is of the document
  /**
   * @csharptype double
   * @scriptsharptype double
   * @cpptype double
   * @cppedgetype double
   */
  percentage: number;
}

/** LanguageCountAnnotation with language localization and localized cultures*/
export interface ILanguageCountAnnotationLocalized
  extends ILanguageCountAnnotation {
  /** Dictionary of cultures: [RFC 4646 tag] to localization */
  cultureInfo: { [culture: string]: string };

  /** Localized display name of the language (based on uiLanguage) */
  languageLocalized: string;
}

/**
 * Represents a definition and description of the text range
 */
export interface IDefinition extends IRangeAnnotation {
  /** Expanded version of the definition. */
  readonly name: string;

  /**
   * Original text of the definition
   * While one could argue that this can be computed from range and content of the parent tile, this is
   * used as optimization technique for invalidating results faster in the future and for reduce workflow which
   * does not have access to content of the tile
   */
  readonly text: string;
}

// Suggestion
// A suggestion for a suggestions annotation. Usually suggests a replacement of a text span with another
export interface ISuggestion {
  // The suggestion text (that can replace the annotation)
  readonly text: string;

  // The description of the suggestions (why it is suggested, its meaning, its definitions, etc.)
  readonly description?: string;

  // Actions that a suggestion supports expressed as flags that can be combined.
  readonly suggestionActions?: SuggestionAction;

  // The language of the suggestion (enabling critiques having multilingual suggestions.)
  readonly languageId?: string;
}

// Neural Rewrite Suggestion
export interface INeuralRewriteSuggestion extends ISuggestion {
  // Each model returns different name such as 'fluency' or 'phrasal'
  readonly modelName?: string;

  // localized model label
  readonly modelLabel?: string;

  // Model version
  readonly modelVersion?: string;
}

// SuggestionsAnnotation
// Represents an annotation on a range of text that has suggestions
export interface ISuggestionsAnnotation extends IRangeAnnotation {
  // The ID of the config that generated this ID
  readonly suggestions: ISuggestion[];
}

// Critique Annotation
export interface ICritique extends ISuggestionsAnnotation {
  // The priority of this critique
  readonly priority: Priority;

  // The context of the annotation
  readonly context: string;

  // The start index of the annotation in this context
  readonly startInContext: number;

  // The name of the category of the critique (Grammar, Spelling, Rewrite, Consistency, etc.)
  readonly categoryName: string;

  // The language of the annotation
  readonly language?: string;

  // The explanation title of the critique
  readonly explanationTitle?: string;

  // The explanation body of the critique
  readonly explanationBody?: string;

  // The explanation label of the critique
  readonly explanationLabel?: string;

  // The bucket ID to group critiques together
  readonly bucketId?: number;

  // Id of the critique type which generated the critique
  readonly typeId?: string;

  // Actions that a critique supports expressed as flags that can be combined.
  readonly critiqueActions?: CritiqueAction;

  // The metadata of the critique
  readonly critiqueMetadata?: { [name: string]: string };
}

// Neural Rewrite Critique Annotation
export interface INeuralRewriteCritique extends ICritique {
  // Optional tracking/correlation ID.
  readonly trackId?: string;

    // Neural rewrite suggestions
    readonly neuralRewriteSuggestions: INeuralRewriteSuggestion[];

    // Editor response status code.
    readonly editorResponseStatusCode: number;
}

// CritiqueCount Annotation
// Annotation with a summary (count) of all the critiques found in the document (grouped by categoryTitle)
export interface ICritiqueCategoryCountAnnotation extends IAnnotation {
  /** The name of the category of the critique (Conciseness, Formality, Inclusiveness, etc.) */
  categoryName: string;

  /** How many instances of the given critique are in the document */
  count: number;

  /** Counts by type id, in sum are equal to count */
  countsByTypeId?: { [typeId: string]: number };
}

/**
 * Annotation with a number of unique words in the document
 */
export interface IUniqueWordsCountAnnotation extends IAnnotation {
  /** Number of unique words in the document */
  count: number;
}

/**
 * Annotation signaling if the document is empty
 */
export interface IEmptyDocumentAnnotation extends IAnnotation {
  /** Boolean indicating if the document contains any content */
  isEmpty: boolean;
}

/**
 * Annotation with all of the personalization settings for the ideas pane
 */
export interface IIdeasPersonalizationSettingsAnnotation extends IAnnotation {
  /** Boolean indicating if we should suppress the auto launch of ideas pane for this user */
  isIdeasPaneTriggerLaunchSuppressed: boolean;
}


/**
 * Annotation with audio data for read aloud feature.
 */
export interface ITextToSpeechAnnotation extends IAnnotation {
  readonly audioData: string[];
  readonly isValidTTS?: boolean;
}

/**
 * Annotation for spelling corrections that should be automatically applied
 * Note: this only exists so it can be activated independently of Critique type until configs can support this scenario
 */
export interface IAutoreplaceAnnotation extends ICritique {}
